import React, { useState, useEffect } from 'react';
import { wholesaleService } from '../../services/wholesaleService.js';
import { emailLeadCategorizer } from '../../services/emailLeadCategorizer.js';
import { getFirestore, collection, getDocs, query, where, orderBy } from 'firebase/firestore';

const WholesaleLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualAddress, setManualAddress] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Email processing state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailFrom, setEmailFrom] = useState('');
  const [emailSource, setEmailSource] = useState('test');
  const [processingEmail, setProcessingEmail] = useState(false);

  // Load wholesale and email leads from Firebase
  const loadLeads = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      
      // Get wholesale leads
      const wholesaleQuery = query(
        collection(db, 'leads'),
        where('type', '==', 'wholesale'),
        orderBy('createdAt', 'desc')
      );
      
      // Get email leads
      const emailQuery = query(
        collection(db, 'leads'),
        where('type', '==', 'email_lead'),
        orderBy('createdAt', 'desc')
      );
      
      const [wholesaleSnapshot, emailSnapshot] = await Promise.all([
        getDocs(wholesaleQuery),
        getDocs(emailQuery)
      ]);
      
      const wholesaleLeads = wholesaleSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const emailLeads = emailSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Combine and sort by creation date
      const allLeads = [...wholesaleLeads, ...emailLeads].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setLeads(allLeads);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
    setLoading(false);
  };

  // Process manual address entry
  const processManualProperty = async () => {
    if (!manualAddress.trim()) return;
    
    setProcessing(true);
    try {
      const result = await wholesaleService.addManualProperty(manualAddress.trim());
      if (result) {
        setManualAddress('');
        loadLeads(); // Reload to show new lead
        alert('Property processed successfully!');
      } else {
        alert('Failed to process property. Check console for details.');
      }
    } catch (error) {
      console.error('Processing error:', error);
      alert('Error processing property');
    }
    setProcessing(false);
  };

  // Process email input
  const processTestEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) return;
    
    setProcessingEmail(true);
    try {
      const result = await emailLeadCategorizer.processEmail({
        subject: emailSubject,
        body: emailBody,
        from: emailFrom || 'test@example.com',
        source: emailSource
      });
      
      if (result) {
        loadLeads(); // Reload to show new lead
        alert(`Email processed! Category: ${result.category}, Address: ${result.address}`);
        // Clear form
        setEmailSubject('');
        setEmailBody('');
        setEmailFrom('');
      } else {
        alert('Email processed but no lead created (duplicate or error)');
      }
    } catch (error) {
      console.error('Email processing error:', error);
      alert('Error processing email');
    }
    setProcessingEmail(false);
  };

  // Load sample FSBO email
  const loadSampleFSBO = () => {
    setEmailSubject('FSBO: 3BR/2BA House - Must Sell Fast!');
    setEmailBody(`Beautiful 3 bedroom, 2 bathroom house for sale by owner.
Located at 1234 Main Street, Sacramento, CA 95814.
Asking $450,000 - motivated seller, needs quick closing.
Property needs some TLC but great bones. Cash offers preferred.
Contact John Smith at (916) 555-0123 or johnsmith@email.com

Details:
- 1,200 sq ft
- As-is condition
- No realtor fees
- Can close in 2 weeks`);
    setEmailFrom('johnsmith@email.com');
    setEmailSource('zillow');
  };

  // Load sample fixer upper email
  const loadSampleFixer = () => {
    setEmailSubject('Handyman Special - Great Investment Opportunity!');
    setEmailBody(`Investment property available in Sacramento!
Property address: 5678 Oak Avenue, Sacramento, CA 95820
Price: $325,000 (below market value)

This 4 bed, 2 bath home needs significant work but has great potential.
Perfect for flippers or rental investors. Sold as-is, cash buyers preferred.

Contact Mary Johnson for details: (916) 555-7890
Email: mary.realtor@gmail.com

Property details:
- 1,400 sq ft
- Needs new roof, flooring, kitchen
- Large lot with room for expansion
- Quick closing possible`);
    setEmailFrom('mary.realtor@gmail.com');
    setEmailSource('craigslist');
  };

  // Export to CSV
  const exportToCSV = () => {
    if (leads.length === 0) return;
    
    // Enhanced CSV headers for both wholesale and email leads
    const headers = [
      'Type', 'Category', 'Address', 'City', 'State', 'Zip', 'Owner_Name', 
      'Phone', 'Email', 'Price', 'Score/Priority', 'Source', 'Date_Found', 'Lead_ID'
    ];

    const csvRows = leads.map(lead => [
      lead.type === 'email_lead' ? 'Email' : 'Wholesale',
      lead.category || '',
      lead.address || '',
      lead.city || '',
      lead.state || '',
      lead.zip || '',
      lead.name || '',
      lead.phone || '',
      lead.email || '',
      lead.formattedPrice || lead.price || 0,
      lead.categoryPriority || lead.wholesaleScore || 0,
      lead.emailSource || lead.source || '',
      lead.createdAt || '',
      lead.id || ''
    ]);

    const csvContent = [headers, ...csvRows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadLeads();
  }, []);

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>🏠 Wholesale Leads</h1>
      
      {/* Manual Property Entry */}
      <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
        <h3>Add Property Manually</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            value={manualAddress}
            onChange={(e) => setManualAddress(e.target.value)}
            placeholder="Enter full address (e.g., 123 Main St, Sacramento, CA 95814)"
            style={{ flex: 1, padding: '8px', fontSize: '16px' }}
          />
          <button 
            onClick={processManualProperty}
            disabled={processing}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: processing ? 'not-allowed' : 'pointer'
            }}
          >
            {processing ? 'Processing...' : 'Process Property'}
          </button>
        </div>
        <p style={{ fontSize: '14px', color: '#666', margin: '8px 0 0 0' }}>
          This will lookup property data with Melissa, get contact info with BatchData, and save to your CRM.
        </p>
      </div>

      {/* Email Lead Processing */}
      <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #28a745', borderRadius: '8px', backgroundColor: '#f8fff9' }}>
        <h3>📧 Process Email Leads</h3>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
          Test the email categorization system. Paste in FSBO alerts, Craigslist emails, or other property emails.
        </p>
        
        {/* Sample email buttons */}
        <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
          <button onClick={loadSampleFSBO} style={{ padding: '6px 12px', backgroundColor: '#ffc107', border: 'none', borderRadius: '4px' }}>
            Load FSBO Sample
          </button>
          <button onClick={loadSampleFixer} style={{ padding: '6px 12px', backgroundColor: '#fd7e14', border: 'none', borderRadius: '4px' }}>
            Load Fixer Sample
          </button>
        </div>

        {/* Email form */}
        <div style={{ display: 'grid', gap: '10px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Email subject"
              style={{ padding: '8px', fontSize: '14px' }}
            />
            <input
              type="text"
              value={emailFrom}
              onChange={(e) => setEmailFrom(e.target.value)}
              placeholder="From email (optional)"
              style={{ padding: '8px', fontSize: '14px' }}
            />
          </div>
          
          <textarea
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            placeholder="Email body content..."
            rows={8}
            style={{ padding: '8px', fontSize: '14px', resize: 'vertical' }}
          />
          
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select 
              value={emailSource} 
              onChange={(e) => setEmailSource(e.target.value)}
              style={{ padding: '8px', fontSize: '14px' }}
            >
              <option value="test">Test</option>
              <option value="zillow">Zillow</option>
              <option value="craigslist">Craigslist</option>
              <option value="facebook">Facebook</option>
              <option value="other">Other</option>
            </select>
            
            <button 
              onClick={processTestEmail}
              disabled={processingEmail || !emailSubject || !emailBody}
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: (!emailSubject || !emailBody || processingEmail) ? 'not-allowed' : 'pointer'
              }}
            >
              {processingEmail ? 'Processing...' : 'Process Email'}
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button onClick={loadLeads} disabled={loading} style={{ padding: '8px 16px' }}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        <button 
          onClick={exportToCSV} 
          disabled={leads.length === 0}
          style={{ 
            padding: '8px 16px', 
            backgroundColor: '#28a745', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px' 
          }}
        >
          Export CSV ({leads.length} leads)
        </button>
        <span style={{ marginLeft: '10px', color: '#666' }}>
          Total: {leads.length} leads ({leads.filter(l => l.type === 'email_lead').length} email, {leads.filter(l => l.type === 'wholesale').length} wholesale)
        </span>
      </div>

      {/* Leads Table */}
      {leads.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={tableHeaderStyle}>Type</th>
                <th style={tableHeaderStyle}>Category</th>
                <th style={tableHeaderStyle}>Score</th>
                <th style={tableHeaderStyle}>Address</th>
                <th style={tableHeaderStyle}>Owner</th>
                <th style={tableHeaderStyle}>Phone</th>
                <th style={tableHeaderStyle}>Email</th>
                <th style={tableHeaderStyle}>Price</th>
                <th style={tableHeaderStyle}>Source</th>
                <th style={tableHeaderStyle}>Date</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead, index) => {
                const isEmailLead = lead.type === 'email_lead';
                const score = isEmailLead ? (lead.categoryPriority || 0) : (lead.wholesaleScore || 0);
                const category = isEmailLead ? lead.category : 'Wholesale';
                
                return (
                  <tr key={lead.id} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                    <td style={tableCellStyle}>
                      <span style={{
                        backgroundColor: isEmailLead ? '#17a2b8' : '#6c757d',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {isEmailLead ? 'Email' : 'Manual'}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      {isEmailLead && lead.categoryColor ? (
                        <span style={{
                          backgroundColor: lead.categoryColor,
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {category}
                        </span>
                      ) : category}
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{
                        backgroundColor: score >= 7 ? '#dc3545' : score >= 5 ? '#ffc107' : '#28a745',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>
                        {score}
                      </span>
                    </td>
                    <td style={tableCellStyle}>{lead.address}</td>
                    <td style={tableCellStyle}>{lead.name || 'N/A'}</td>
                    <td style={tableCellStyle}>
                      {lead.phone ? (
                        <a href={`tel:${lead.phone}`} style={{ color: '#007bff' }}>
                          {lead.phone}
                        </a>
                      ) : 'N/A'}
                    </td>
                    <td style={tableCellStyle}>
                      {lead.email ? (
                        <a href={`mailto:${lead.email}`} style={{ color: '#007bff' }}>
                          {lead.email.length > 20 ? lead.email.substring(0, 20) + '...' : lead.email}
                        </a>
                      ) : 'N/A'}
                    </td>
                    <td style={tableCellStyle}>
                      {lead.formattedPrice || lead.formattedEstimatedValue || (lead.price ? `$${lead.price.toLocaleString()}` : 'N/A')}
                    </td>
                    <td style={tableCellStyle}>{lead.emailSource || lead.source || 'N/A'}</td>
                    <td style={tableCellStyle}>
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {loading ? 'Loading leads...' : 'No wholesale leads found. Add a property manually to get started!'}
        </div>
      )}
    </div>
  );
};

const tableHeaderStyle = {
  padding: '12px 8px',
  textAlign: 'left',
  borderBottom: '2px solid #dee2e6',
  fontWeight: 'bold'
};

const tableCellStyle = {
  padding: '8px',
  borderBottom: '1px solid #dee2e6',
  maxWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

export default WholesaleLeads;